// Секция «Терминал плотностей»: filtre sus, grafic stânga, tabel de ziduri dreapta.
// Zidul selectat se desenează pe grafic ca linie de preț cu eticheta mărimii.

import { el, clear, bus, debounce, fmtVolume, fmtPct, fmtAge, fmtPrice, priceDigits } from '../core/util.js';
import { iconMoneda } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { settings, market as marketData } from '../core/state.js';
import { ChartPanel } from '../ui/chart-panel.js';
import { baseTicker, toast } from '../ui/components.js';
import { DensityMap } from '../ui/density-map.js';
import { DensityLayer } from '../chart/density-layer.js';

const REFRESH_MS = 2000;

/**
 * Chip-uri independente, nu taburi exclusive — asta e diferența față de cum erau la noi.
 * Se pot combina: «Покупки» + «Новые» arată doar zidurile de cumpărare apărute recent.
 * «Следовать» e un mod separat (verde la ei), nu un filtru: lista urmează moneda de pe grafic.
 */
const CHIPS = [
  { id: 'bid', label: 'Покупки' },
  { id: 'ask', label: 'Продажи' },
  { id: 'fresh', label: 'Новые' },
  { id: 'favorites', label: 'Избранные' },
];

const FOLLOW_CHIP = { id: 'following', label: 'Следовать' };

/** Sub acest prag zidul e „nou" — la ei rândurile de 7–46 s sunt marcate ca proaspete. */
const FRESH_SECONDS = 60;
/** Peste acest prag durata se colorează verde: zidul chiar stă. */
const OLD_SECONDS = 120;

const COLUMNS = [
  { id: 'symbol', label: 'МОНЕТА', sort: (a, b) => a.symbol.localeCompare(b.symbol) },
  { id: 'sizeUsd', label: 'ОБЪЁМ', sort: (a, b) => b.sizeUsd - a.sizeUsd },
  { id: 'distance', label: 'РАССТ.', sort: (a, b) => Math.abs(a.distance) - Math.abs(b.distance) },
  { id: 'age', label: 'ЖИВЁТ', sort: (a, b) => b.age - a.age },
  { id: 'ratio', label: 'СИЛА', sort: (a, b) => (b.ratio || 0) - (a.ratio || 0) },
];

const DEFAULTS = {
  market: 'futures',
  minAge: 0,
  minSize: 150_000,
  maxDistance: 5,
  noHeavyweights: false,
  cryptoOnly: false,
};

/** Urmele zidurilor mâncate, păstrate în browser: câte pe monedă și cât de vechi. */
const TRACES_KEY = 'sx_wall_traces';
const TRACES_PER_COIN = 40;
const TRACES_MAX_AGE = 24 * 3600 * 1000;
const COIN_REFRESH_MS = 2500;

function citesteUrme() {
  try {
    return JSON.parse(localStorage.getItem(TRACES_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/** Simboluri „grele" pe care filtrul „Без тяжеловесов" le scoate. */
const HEAVYWEIGHTS = new Set(['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT']);
/** Acțiuni/indici/metale tokenizate — filtrul „Только криптовалюты" le scoate. */
const NON_CRYPTO = /^(XAU|XAG|XPT|XPD|PAXG|SPX|NAS|US30|GER|UK|HK|JPN|AAPL|MSFT|NVDA|TSLA|AMZN|GOOGL|META|NFLX|ORCL|INTC|AMD|COIN|MSTR|QQQ|SPY|DIA|IWM|NATGAS|COPPER|CL)/;

export class DensitySection {
  constructor(app) {
    this.app = app;
    this.chips = new Set(settings.get('sx_density_chips', []) || []);
    this.following = false;
    this.filters = { ...DEFAULTS, ...(settings.get('sx_density_filters', {}) || {}) };
    this.sort = { column: 'distance', desc: false };
    this.search = '';
    this.walls = [];
    this.selected = null;
    this.priceLines = [];
    this.firstSeenAt = Date.now();
    this.unsubs = [];
    // „Карта" e modul implicit: acolo se văd monedele volatile, nu doar BTC/ETH.
    this.mode = settings.get('sx_density_mode', 'map');
  }

  mount(host) {
    this.root = el('div', { class: 'density-section' });
    this.root.append(this.#filterBar());

    const body = el('div', { class: 'density-body' });
    this.chartHost = el('div', { class: 'density-chart' });
    this.tableEl = this.#table();
    this.mapHost = el('div', { class: 'density-map' });
    body.append(this.chartHost, this.tableEl, this.mapHost);
    this.root.append(body);
    host.append(this.root);
    this.map = new DensityMap({ onSelect: (w) => this.#selectCoin(w.ex, w) }).mount(this.mapHost);
    this.#applyMode();

    this.panel = new ChartPanel({
      exchange: 'binance',
      market: this.filters.market === 'spot' ? 'spot' : 'futures',
      symbol: settings.get('sx_coins_symbol', 'BTCUSDT'),
      tf: '1m',
    }).mount(this.chartHost);

    this.timer = setInterval(() => this.#fetch(), REFRESH_MS);
    // Zidurile monedei de pe grafic: vii și mâncate — independent de filtrele hărții/listei.
    this.coinTimer = setInterval(() => this.#loadCoin(), COIN_REFRESH_MS);
    this.#fetch();
  }

  unmount() {
    this.map?.unmount();
    clearInterval(this.timer);
    clearInterval(this.coinTimer);
    this.unsubs.forEach((fn) => fn());
    this.panel?.destroy();
    this.root?.remove();
  }

  // ------------------------------------------------------------------ filtre

  /** „Список" (vechiul terminal) sau „Карта" (harta densităților). */
  #applyMode() {
    const harta = this.mode === 'map';
    this.root.classList.toggle('is-map', harta);
    this.tableEl.hidden = harta;
    this.mapHost.hidden = !harta;
    this.listTools.hidden = harta;
    for (const b of this.modeEl.children) b.classList.toggle('active', b.dataset.id === this.mode);
    if (!harta) this.#fetch();
  }

  /**
   * Moneda aleasă (din hartă sau din listă, pe futures) ajunge pe grafic cu stratul de
   * densități: fiecare zid viu e un segment de la apariție până acum, cu vârsta care crește
   * singură; cel mâncat lasă o urmă portocaliu închis.
   */
  async #selectCoin(ex, wall) {
    this.#clearWalls();
    this.coinSel = { ex, symbol: wall.symbol, price: wall.price, side: wall.side, gone: false };
    this.layer?.clear();
    if (this.panel.exchange !== ex || this.panel.market !== 'futures') await this.panel.setExchange(ex, 'futures');
    if (this.panel.symbol !== wall.symbol) await this.panel.setSymbol(wall.symbol);
    await this.#loadCoin();
  }

  #ensureLayer() {
    const chart = this.panel?.chart;
    if (!chart) return null;
    if (!this.layer || this.layer.chart !== chart) this.layer = chart.addLayer(new DensityLayer(chart));
    return this.layer;
  }

  async #loadCoin() {
    const sel = this.coinSel;
    if (!sel || this.panel?.symbol !== sel.symbol) return;
    let d;
    try {
      const q = new URLSearchParams({ exchange: sel.ex, symbol: sel.symbol });
      d = await (await fetch(`/api/walls/coin?${q}`)).json();
    } catch {
      return;
    }
    if (this.coinSel !== sel) return;          // între timp s-a ales altă monedă
    const key = `${sel.ex}|${sel.symbol}`;
    const toate = citesteUrme();
    const urme = new Map((toate[key] || []).map((t) => [`${t.price}|${t.first}`, t]));
    let noi = false;
    for (const w of d.ended || []) {
      if (w.fate !== 'eaten' || !w.ended) continue;
      const id = `${w.price}|${w.first}`;
      if (!urme.has(id)) {
        urme.set(id, { price: w.price, side: w.side, first: w.first, ended: w.ended, peak: w.peak });
        noi = true;
      }
    }
    const acum = Date.now();
    const lista = [...urme.values()].filter((t) => acum - t.ended < TRACES_MAX_AGE)
      .sort((a, b) => b.ended - a.ended).slice(0, TRACES_PER_COIN);
    if (noi) {
      toate[key] = lista;
      try { localStorage.setItem(TRACES_KEY, JSON.stringify(toate)); } catch { /* stocare plină — urmele rămân doar în sesiune */ }
    }
    // Zidul pe care s-a dat clic a dispărut: spunem o singură dată ce s-a întâmplat cu el.
    if (!sel.gone && !(d.walls || []).some((x) => x.price === sel.price && x.side === sel.side)) {
      const fin = (d.ended || []).find((x) => x.price === sel.price && x.side === sel.side);
      if (fin) {
        sel.gone = true;
        toast(`${baseTicker(sel.symbol)} ${fmtPrice(sel.price)}: плотность ${fin.fate === 'eaten' ? 'съедена — на графике осталась оранжевая линия' : 'снята до подхода цены'}`, 'info');
      }
    }
    this.#ensureLayer()?.setData(d.walls || [], lista, { price: sel.price, side: sel.side });
  }

  #filterBar() {
    const bar = el('div', { class: 'density-bar' });
    this.modeEl = el('div', { class: 'fx-seg density-mode' }, [['map', 'Карта', 'radar'], ['list', 'Список', 'rows']]
      .map(([id, t, ic]) => el('button', {
        class: 'fx-seg-btn', dataset: { id },
        onclick: () => { this.mode = id; settings.set('sx_density_mode', id); this.#applyMode(); },
      }, [icon(ic, 12), el('span', { text: t })])));

    const check = (label, key) => el('label', { class: 'density-check' }, [
      el('input', {
        type: 'checkbox',
        checked: this.filters[key],
        onchange: (e) => { this.filters[key] = e.target.checked; this.#persist(); this.#render(); },
      }),
      el('span', { text: label }),
    ]);

    const numberField = (label, key, { step = 1, suffix = '' } = {}) =>
      el('div', { class: 'density-field' }, [
        el('span', { class: 'density-field-label', text: label }),
        el('input', {
          class: 'density-input', type: 'number', value: this.filters[key], step,
          oninput: debounce((e) => {
            this.filters[key] = Number(e.target.value) || 0;
            this.#persist();
            this.#fetch();
          }, 250),
        }),
        suffix ? el('span', { class: 'density-field-suffix', text: suffix }) : null,
      ]);

    const marketGroup = el('div', { class: 'market-toggle' },
      [['all', 'Все'], ['futures', 'Фьючерсы'], ['spot', 'Спот']].map(([id, label]) => el('button', {
        class: `market-btn${this.filters.market === id ? ' active' : ''}`,
        text: label,
        onclick: (event) => {
          this.filters.market = id;
          [...marketGroup.children].forEach((c) => c.classList.remove('active'));
          event.currentTarget.classList.add('active');
          this.#persist();
          if (id !== 'all') this.panel?.setExchange('binance', id);
          this.#fetch();
        },
      })));

    // Distanța e slider la ScalpX, nu câmp numeric — se reglează din mers.
    const distanceValue = el('span', {
      class: 'density-field-suffix', text: `до ${this.filters.maxDistance}%`,
    });
    const distance = el('div', { class: 'density-field' }, [
      el('span', { class: 'density-field-label', text: 'РАССТОЯНИЕ' }),
      el('input', {
        type: 'range', class: 'density-range', min: 0.1, max: 10, step: 0.1,
        value: this.filters.maxDistance,
        oninput: (e) => {
          this.filters.maxDistance = Number(e.target.value);
          distanceValue.textContent = `до ${this.filters.maxDistance}%`;
          this.#persist();
          this.#renderDebounced();
        },
      }),
      distanceValue,
    ]);

    this.listTools = el('div', { class: 'density-list-tools' });
    bar.append(this.modeEl, this.listTools);
    this.listTools.append(
      check('Без тяжеловесов', 'noHeavyweights'),
      check('Только криптовалюты', 'cryptoOnly'),
      el('div', { class: 'bar-sep' }),
      numberField('СТОИТ ДОЛЬШЕ', 'minAge', { suffix: 'сек' }),
      numberField('РАЗМЕР ОТ', 'minSize', { step: 10000, suffix: '$' }),
      distance,
      el('div', { class: 'bar-sep' }),
      marketGroup,
      el('button', { class: 'bar-icon-btn', title: 'Обновить', onclick: () => this.#fetch() }, [icon('refresh', 15)]),
      this.countEl = el('span', { class: 'density-count' }),
    );
    return bar;
  }

  #persist() {
    settings.set('sx_density_filters', this.filters);
  }

  /** Slider-ul trage des; refacem cererea doar când s-a oprit. */
  #renderDebounced = debounce(() => this.#fetch(), 200);

  // ------------------------------------------------------------------ tabel

  #table() {
    const chip = (spec, mode = false) => el('button', {
      class: `dterm-chip${mode ? ' is-mode' : ''}${this.#chipOn(spec.id) ? ' is-on' : ''}`,
      text: spec.label,
      onclick: (event) => {
        if (mode) {
          this.following = !this.following;
          if (this.following) this.#syncFollow();
        } else if (this.chips.has(spec.id)) this.chips.delete(spec.id);
        else this.chips.add(spec.id);
        settings.set('sx_density_chips', [...this.chips]);
        event.currentTarget.classList.toggle('is-on', this.#chipOn(spec.id));
        this.#render();
      },
      dataset: { id: spec.id },
    });

    this.tabsEl = el('div', { class: 'dterm-chips' }, [
      ...CHIPS.map((c) => chip(c)),
      chip(FOLLOW_CHIP, true),
    ]);

    this.headEl = el('div', { class: 'density-head' }, COLUMNS.map((col) => el('button', {
      class: `density-th${this.sort.column === col.id ? ' sorted' : ''}`,
      onclick: () => {
        if (this.sort.column === col.id) this.sort.desc = !this.sort.desc;
        else { this.sort.column = col.id; this.sort.desc = false; }
        this.#render();
      },
      dataset: { id: col.id },
    }, [el('span', { text: col.label }), icon('arrowDown', 9)])));

    this.rowsEl = el('div', { class: 'density-rows' });

    return el('div', { class: 'density-panel' }, [
      this.tabsEl,
      el('div', { class: 'density-search' }, [
        icon('search', 13),
        el('input', {
          class: 'search-input',
          placeholder: 'Поиск монеты...',
          oninput: debounce((e) => { this.search = e.target.value.trim().toUpperCase(); this.#render(); }, 180),
        }),
      ]),
      this.headEl,
      this.rowsEl,
    ]);
  }

  // -------------------------------------------------------------------- date

  async #fetch() {
    if (this.mode === 'map') return;     // harta își citește singură datele
    const f = this.filters;
    const params = new URLSearchParams({
      market: f.market,
      minSize: f.minSize,
      maxDistance: f.maxDistance,
      minAge: f.minAge,
      limit: 300,
    });
    try {
      const resp = await fetch(`/api/density?${params}`);
      const data = await resp.json();
      this.walls = data.walls || [];
      this.tracked = data.tracked || 0;
      this.#render();
    } catch (err) {
      console.warn('[density]', err);
    }
  }

  #chipOn(id) {
    return id === 'following' ? this.following : this.chips.has(id);
  }

  /** Cu «Следовать» pornit, lista arată doar moneda de pe grafic. */
  #syncFollow() {
    this.#render();
  }

  #visible() {
    let rows = this.walls;

    // Chip-urile de latură se combină: niciunul sau amândouă înseamnă „ambele".
    const wantBid = this.chips.has('bid');
    const wantAsk = this.chips.has('ask');
    if (wantBid !== wantAsk) rows = rows.filter((w) => w.side === (wantBid ? 'bid' : 'ask'));

    if (this.chips.has('fresh')) rows = rows.filter((w) => w.age < FRESH_SECONDS);
    if (this.chips.has('favorites')) {
      const marks = new Set(settings.get('sx_bookmarks', []) || []);
      rows = rows.filter((w) => marks.has(w.symbol));
    }
    if (this.following) rows = rows.filter((w) => w.symbol === this.panel?.symbol);

    if (this.filters.noHeavyweights) rows = rows.filter((w) => !HEAVYWEIGHTS.has(w.symbol));
    if (this.filters.cryptoOnly) rows = rows.filter((w) => !NON_CRYPTO.test(w.symbol));
    if (this.search) rows = rows.filter((w) => w.symbol.includes(this.search));

    const column = COLUMNS.find((c) => c.id === this.sort.column) || COLUMNS[2];
    rows = [...rows].sort(column.sort);
    if (this.sort.desc) rows.reverse();
    return rows;
  }

  #render() {
    if (!this.rowsEl) return;
    const rows = this.#visible();
    this.countEl.textContent = `${rows.length} из ${this.tracked ?? 0}`;

    for (const th of this.headEl.children) {
      th.classList.toggle('sorted', th.dataset.id === this.sort.column);
      th.classList.toggle('desc', this.sort.desc);
    }

    const scroll = this.rowsEl.scrollTop;
    clear(this.rowsEl);

    if (!rows.length) {
      this.rowsEl.append(el('div', { class: 'density-empty', text: 'Плотностей по этим условиям нет' }));
      return;
    }

    const marks = new Set(settings.get('sx_bookmarks', []) || []);
    for (const wall of rows) {
      const key = `${wall.symbol}:${wall.side}:${wall.price}`;
      const buy = wall.side === 'bid';
      const classes = [
        'dpanel-row',
        buy ? 'is-long' : 'is-short',
        wall.age < FRESH_SECONDS ? 'is-fresh' : '',
        this.selected === key ? 'is-selected' : '',
      ].filter(Boolean).join(' ');

      this.rowsEl.append(el('div', {
        class: classes,
        title: buy
          ? `Покупка (поддержка) · уровень ${wall.price}`
          : `Продажа (сопротивление) · уровень ${wall.price}`,
        onclick: () => this.#select(wall, key),
      }, [
        el('span', {
          class: `dpc-fav${marks.has(wall.symbol) ? ' is-on' : ''}`,
          title: 'В избранное',
          onclick: (event) => {
            event.stopPropagation();
            const set = new Set(settings.get('sx_bookmarks', []) || []);
            if (set.has(wall.symbol)) set.delete(wall.symbol); else set.add(wall.symbol);
            settings.set('sx_bookmarks', [...set]);
            event.currentTarget.classList.toggle('is-on', set.has(wall.symbol));
          },
        }, [icon(marks.has(wall.symbol) ? 'starOn' : 'star', 10)]),
        // Iconița și tickerul într-o singură celulă: separat erau 7 elemente pe o grilă
        // de 6 coloane — totul se decala cu o coloană, iar cota cădea pe rândul următor.
        el('span', { class: 'dpc-coin' }, [
          iconMoneda(wall.symbol, 18),
          el('span', { class: 'dpc-ticker', text: baseTicker(wall.symbol) }),
        ]),
        el('span', { class: 'dpc-amount', text: fmtVolume(wall.sizeUsd) }),
        el('span', {
          class: 'dpc-dist',
          text: `${wall.distance >= 0 ? '+' : '−'}${Math.abs(wall.distance).toFixed(2)}%`,
        }),
        el('span', {
          class: `dpc-age${wall.age >= OLD_SECONDS ? ' is-old' : ''}`,
          text: fmtAge(wall.age),
        }),
        el('span', {
          class: 'dpc-share',
          title: 'Во сколько раз плотность больше обычного уровня этой монеты',
          text: wall.ratio ? `×${wall.ratio}` : `${(wall.share * 100).toFixed(2)}%`,
        }),
      ]));
    }
    this.rowsEl.scrollTop = scroll;
    if (this.selected && this.spotSel && this.panel?.symbol) this.#drawWalls(this.panel.symbol);
  }

  /** Deschide moneda pe grafic: futures — cu stratul de densități, spot — cu linii de preț. */
  async #select(wall, key) {
    this.selected = key;
    if (wall.market === 'futures') {
      this.spotSel = false;
      await this.#selectCoin('binance', wall);
      this.#render();
      return;
    }
    this.spotSel = true;
    this.coinSel = null;
    this.layer?.clear();
    if (this.panel.exchange !== 'binance' || this.panel.market !== 'spot') await this.panel.setExchange('binance', 'spot');
    if (this.panel.symbol !== wall.symbol) {
      // Liniile vechi aparțin altui interval de preț — le scoatem înainte de comutare,
      // altfel rămân pe grafic cât durează încărcarea noilor lumânări.
      this.#clearWalls();
      await this.panel.setSymbol(wall.symbol);
    }
    this.#drawWalls(wall.symbol);
    this.#render();
  }

  #clearWalls() {
    const series = this.panel?.chart?.candles;
    if (!series) return;
    this.priceLines.forEach((line) => series.removePriceLine(line));
    this.priceLines = [];
  }

  #drawWalls(symbol) {
    const series = this.panel?.chart?.candles;
    if (!series) return;
    this.#clearWalls();

    for (const wall of this.walls.filter((w) => w.symbol === symbol)) {
      this.priceLines.push(series.createPriceLine({
        price: wall.price,
        color: wall.side === 'bid' ? '#26a69a' : '#ef5350',
        lineWidth: 2,
        lineStyle: 0,
        axisLabelVisible: true,
        title: fmtVolume(wall.sizeUsd),
      }));
    }
  }
}
